<template>
  <v-container fluid class="booking-view pa-0">
    <section class="categories-section">
      <v-container>
        <v-row class="mb-8">
          <v-col cols="12">
            <div class="section-header">
              <div class="section-eyebrow">Browse by Category</div>
              <h2 class="section-label">Find the Right Service Fast</h2>
              <p class="section-subtitle">
                Choose a service to explore curated professionals
              </p>
            </div>
          </v-col>
        </v-row>

        <div v-if="!userLocation.allowed" class="location-required-card">
          <v-icon size="34" color="#f46523" class="mb-2">mdi-map-marker-radius</v-icon>
          <h3 class="location-required-title">Location Access Required</h3>
          <p class="location-required-text">
            {{
              userLocation.error ||
              "Please enable location permission to view nearby categories and services."
            }}
          </p>
          <v-btn color="#f46523" dark depressed @click="handleEnableLocationClick">
            Enable Location
          </v-btn>
        </div>

        <div v-else-if="categoriesLoading" class="text-center mb-4">
          <v-progress-circular
            indeterminate
            color="#f46523"
          ></v-progress-circular>
          <p class="mt-2 grey--text">Loading categories...</p>
        </div>

        <div v-else-if="categories.length === 0" class="location-required-card">
          <v-icon size="34" color="#f46523" class="mb-2">mdi-alert-circle-outline</v-icon>
          <h3 class="location-required-title">No categories available</h3>
          <p class="location-required-text">
            We could not load categories right now. Please try again.
          </p>
          <v-btn color="#f46523" dark depressed @click="loadCategoriesWithFallback">
            Retry
          </v-btn>
        </div>

        <div v-else class="categories-rows">
          <div class="categories-grid">
            <div
              v-for="category in categories"
              :key="category._id"
              class="ha-category-item"
              @click="openCategoryPage(category)"
            >
              <div class="ha-icon-shell">
                <div class="ha-icon-ring"></div>
                <div class="ha-icon-core">
                  <v-img
                    v-if="category.image"
                    :src="category.image"
                    height="42"
                    width="42"
                    contain
                    class="ha-icon-image"
                  ></v-img>
                  <v-icon v-else size="30">{{ getCategoryIcon() }}</v-icon>
                </div>
              </div>
              <span class="ha-label">{{ category.name }}</span>
            </div>
          </div>

        </div>
      </v-container>
    </section>

    <LocationPermissionDialog v-model="showLocationHelpDialog" />
  </v-container>
</template>

<script>
import { fetchCategories } from "@/services/api";
import LocationPermissionDialog from "@/components/LocationPermissionDialog.vue";

export default {
  name: "HomeAdvisorSetup",
  components: {
    LocationPermissionDialog,
  },
  data() {
    return {
      userLocation: {
        latitude: null,
        longitude: null,
        allowed: false,
        error: null,
      },
      categories: [],
      categoriesLoading: false,
      pendingCategoryToOpen: null,
      showLocationHelpDialog: false,
      openHelpOnDenied: false,
    };
  },

  mounted() {
    console.log("🚀 Component mounted");
    this.restoreLocationAndInit();
  },

  methods: {
    async handleEnableLocationClick() {
      const isBlocked = await this.isLocationPermissionBlocked();
      if (isBlocked) {
        this.showLocationHelpDialog = true;
        return;
      }

      this.openHelpOnDenied = true;
      this.requestUserLocation();
    },

    async isLocationPermissionBlocked() {
      try {
        if (!navigator.permissions?.query) return false;
        const permission = await navigator.permissions.query({
          name: "geolocation",
        });
        return permission.state === "denied";
      } catch (error) {
        return false;
      }
    },

    getCategoryIcon() {
      return "mdi-tools";
    },
    async restoreLocationAndInit() {
      const savedLocation = localStorage.getItem("userLocation");

      if (savedLocation) {
        this.userLocation = JSON.parse(savedLocation);
      }

      if (this.userLocation.allowed) {
        await this.loadCategoriesWithFallback();
      } else {
        this.requestUserLocation();
      }
    },

    requestUserLocation() {
      this.categoriesLoading = true;
      this.userLocation.error = null;
      if (
        typeof window !== "undefined" &&
        !window.isSecureContext &&
        window.location.hostname !== "localhost"
      ) {
        this.userLocation.error =
          "Location requires a secure connection (HTTPS). Please open this site on HTTPS and retry.";
        this.categoriesLoading = false;
        return;
      }

      if (!navigator.geolocation) {
        this.userLocation.error =
          "Geolocation is not supported by this browser.";
        this.categoriesLoading = false;
        return;
      }

      navigator.geolocation.getCurrentPosition(
        this.onLocationSuccess,
        this.onLocationError,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    },

    async onLocationSuccess(position) {
      this.openHelpOnDenied = false;
      this.userLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        allowed: true,
        error: null,
      };

      localStorage.setItem("userLocation", JSON.stringify(this.userLocation));

      console.log("📍 Location saved:", this.userLocation);

      await this.loadCategoriesWithFallback();

      if (this.pendingCategoryToOpen) {
        const pending = this.pendingCategoryToOpen;
        this.pendingCategoryToOpen = null;
        this.navigateToCategoryPage(pending);
      }
    },

    onLocationError(error) {
      let message = "An unknown location error occurred.";

      switch (error.code) {
        case error.PERMISSION_DENIED:
          message =
            "Location permission is blocked. Enable it in your browser site settings and try again.";
          if (this.openHelpOnDenied) {
            this.showLocationHelpDialog = true;
          }
          break;
        case error.POSITION_UNAVAILABLE:
          message = "Location information is unavailable.";
          break;
        case error.TIMEOUT:
          message = "Location request timed out.";
          break;
      }

      this.userLocation = {
        latitude: null,
        longitude: null,
        allowed: false,
        error: message,
      };

      localStorage.setItem("userLocation", JSON.stringify(this.userLocation));
      console.warn("❌ Location error:", message);
      this.categories = [];
      this.openHelpOnDenied = false;
      this.categoriesLoading = false;
    },

    async loadCategoriesWithFallback() {
      this.categoriesLoading = true;
      try {
        const { latitude, longitude } = this.userLocation;

        console.log("🔍 Fetching categories with location:", {
          latitude,
          longitude,
        });

        const response = await fetchCategories({
          lat: latitude,
          lng: longitude,
        });
        console.log("📦 Full API response:", response);
        console.log("📦 Response data:", response?.data);
        const categoriesWithLocation = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || [];

        console.log("📋 Extracted categories:", categoriesWithLocation);

        if (categoriesWithLocation.length > 0) {
          this.categories = categoriesWithLocation;
          console.log(
            "✅ Categories loaded with location:",
            this.categories.length,
            "categories"
          );
          this.$forceUpdate();
          return;
        }
        console.warn("⚠️ Empty response with location, trying without location");
        await this.loadCategoriesWithoutLocation();
      } catch (error) {
        console.error("❌ Location-based category fetch failed, trying without location", error);
        await this.loadCategoriesWithoutLocation();
      } finally {
        this.categoriesLoading = false;
      }
    },

    async loadCategoriesWithoutLocation() {
      try {
        console.log("🌍 Fetching global categories without location");
        const response = await fetchCategories({});
        console.log("📦 Global response:", response);
        this.categories = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || [];

        console.log(
          "🌍 Global categories loaded:",
          this.categories.length,
          "categories"
        );
        console.log("🌍 Categories array:", this.categories);
        this.$forceUpdate();
      } catch (error) {
        console.error("❌ Failed to load global categories", error);
      }
    },

    openCategoryPage(category) {
      if (!this.userLocation.allowed) {
        this.pendingCategoryToOpen = category;
        this.requestUserLocation();
        return;
      }
      this.navigateToCategoryPage(category);
    },

    navigateToCategoryPage(category) {
      sessionStorage.setItem("selectedCategory", JSON.stringify(category));
      this.$router.push({
        name:
          this.$route.meta?.location === "us" ||
          this.$route.path.startsWith("/us")
            ? "CategoryServicesUS"
            : "CategoryServices",
        params: {
          categoryId: category._id,
          categoryName: category.name,
        },
      });
    },

    scrollToCategories() {
      document
        .querySelector(".categories-section")
        ?.scrollIntoView({ behavior: "smooth" });
    },

  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap");

.booking-view {
  font-family: "Poppins", sans-serif;
  background-color: #f4f7f9;
}
.hero-section {
  position: relative;
  background: white;
  border-bottom: 1px solid #f0f0f0;
}
.cut-pattern {
  position: absolute;
  inset: 0;
  opacity: 0.08;
  background-image: radial-gradient(#f46523 1px, transparent 1px);
  background-size: 26px 26px;
}
.relative-content {
  position: relative;
  z-index: 1;
}
.hero-title {
  font-size: 44px;
  font-weight: 700;
  color: #111;
}
.hero-subtitle {
  font-size: 18px;
  color: #555;
}
.hero-cta {
  height: 52px;
  font-weight: 700;
  border-radius: 10px;
}
.categories-section {
  background: linear-gradient(180deg, #ffffff 0%, #fffdfb 100%);
  padding: 52px 0 70px;
}
.section-header {
  text-align: center;
  margin-bottom: 6px;
}
.section-eyebrow {
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #ff6b35;
  font-weight: 700;
  margin-bottom: 10px;
}
.section-label {
  font-size: 32px;
  font-weight: 900;
  letter-spacing: -0.6px;
  margin-bottom: 10px;
  color: #121212;
}
.section-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0 auto;
  max-width: 560px;
  line-height: 1.6;
}

/* ✅ FIX 2: Center second row by using justify-content: center */
.categories-rows {
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.categories-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 22px 24px;
  justify-content: center;
  align-items: flex-start;
}
.bottom-grid {
  justify-content: center;
}

.ha-category-item {
  text-align: center;
  cursor: pointer;
  padding: 14px 12px 16px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06),
    inset 0 0 0 1px rgba(15, 23, 42, 0.05);
  width: 150px;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease,
    background 0.2s ease;
  flex-shrink: 0;
  min-width: 150px;
  max-width: 150px;
}
.ha-category-item:hover {
  transform: translateY(-2px);
}
.ha-icon-shell {
  width: 92px;
  height: 92px;
  margin: 0 auto 14px;
  position: relative;
  display: grid;
  place-items: center;
}
.ha-icon-ring {
  position: absolute;
  inset: 0;
  border-radius: 28px;
  background: radial-gradient(circle at 30% 30%, #fff1e7, #ffd6c2 60%);
  opacity: 0.28;
  filter: blur(0.5px);
}
.ha-icon-core {
  width: 80px;
  height: 80px;
  border-radius: 24px;
  background: #fff9f5;
  border: 1px solid rgba(255, 107, 53, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 22px rgba(255, 107, 53, 0.16),
    inset 0 0 0 1px rgba(255, 255, 255, 0.8);
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease,
    border-color 0.25s ease;
}
.ha-icon-image .v-image__image {
  filter: brightness(1.06) contrast(1.12) saturate(1.08);
}
.ha-icon-core .v-icon {
  color: #f46523 !important;
}
.ha-category-item:hover .ha-icon-core {
  transform: translateY(-3px) scale(1.03);
  box-shadow: 0 14px 28px rgba(255, 107, 53, 0.22),
    inset 0 0 0 1px rgba(255, 255, 255, 0.9);
}
.ha-category-item.active .ha-icon-core {
  border: 2px solid #ff6b35;
  background: #fff9f5;
  box-shadow: 0 10px 22px rgba(255, 107, 53, 0.18),
    inset 0 0 0 1px rgba(255, 255, 255, 0.85);
}
.ha-category-item.active .ha-icon-core .v-icon {
  color: #ff6b35 !important;
}
.ha-label {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  color: #1f2937;
  display: block;
  letter-spacing: 0.2px;
  text-align: center;
  min-height: 42px;
}
.ha-category-item.active {
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06),
    inset 0 0 0 1px rgba(15, 23, 42, 0.05);
}

/* ✅ FIX 1: White background with green check */
@media (max-width: 1200px) {
  .categories-grid {
    justify-content: center;
  }
}

@media (max-width: 960px) {
  .categories-rows {
    gap: 20px;
  }
  .categories-grid {
    gap: 20px 22px;
  }
}

@media (max-width: 600px) {
  .section-label {
    font-size: 26px;
    line-height: 1.2;
    letter-spacing: -0.2px;
  }
  .section-subtitle {
    font-size: 14px;
    line-height: 1.45;
    padding: 0 8px;
  }
  .categories-rows {
    gap: 16px;
  }
  .categories-grid {
    gap: 12px;
  }
  .ha-category-item {
    padding: 10px 8px 12px;
    width: calc(50% - 6px);
    min-width: 0;
    max-width: none;
    min-height: 126px;
  }
  .ha-icon-shell {
    width: 72px;
    height: 72px;
    margin-bottom: 10px;
  }
  .ha-icon-core {
    width: 62px;
    height: 62px;
    border-radius: 18px;
  }
  .ha-label {
    font-size: 13px;
    line-height: 1.3;
    min-height: 34px;
  }
}

@media (max-width: 380px) {
  .ha-category-item {
    padding: 9px 6px 10px;
    min-height: 118px;
  }
  .ha-icon-shell {
    width: 64px;
    height: 64px;
    margin-bottom: 8px;
  }
  .ha-icon-core {
    width: 56px;
    height: 56px;
  }
  .ha-label {
    font-size: 12px;
  }
}

.ha-category-item:hover .ha-label,
.ha-category-item.active .ha-label {
  color: #f46523;
}

.location-required-card {
  text-align: center;
  background: #fff;
  border: 1px solid #ffe0d1;
  border-radius: 14px;
  padding: 24px 16px;
  margin-bottom: 16px;
}

.location-required-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #1f2937;
}

.location-required-text {
  font-size: 14px;
  color: #6b7280;
  margin: 0 auto 14px;
  max-width: 520px;
}
.results-section {
  padding: 60px 0;
}
.breadcrumb-nav {
  font-size: 14px;
}
.breadcrumb-nav .current {
  color: #f46523;
  font-weight: 600;
}
.results-title {
  font-size: 26px;
  font-weight: 700;
}
.results-count {
  font-size: 14px;
  color: #666;
}
.pro-card {
  height: 100%;
  border-radius: 12px;
}

.service-img {
  flex-shrink: 0;
}

.pro-card .v-card-text {
  margin-top: auto;
}
.pro-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.pro-name {
  font-size: 16px;
  font-weight: 600;
}
.opacity-30 {
  opacity: 0.3;
}
</style>
